import { useState, useEffect } from 'react';
import { Segmented, Input, Tooltip } from 'antd';
import { CalendarOutlined, CoffeeOutlined, TeamOutlined } from '@ant-design/icons';

const STATES = {
    closed: {
        label: 'No gathering',
        icon: <CalendarOutlined />,
        text: 'Members see that there’s no gathering right now, with your message.',
    },
    waiting: {
        label: 'Waiting room',
        icon: <CoffeeOutlined />,
        text: 'Members wait in the waiting room. It opens by itself when you open the gathering.',
    },
    open: {
        label: 'Open',
        icon: <TeamOutlined />,
        text: 'Members pick their groups and see their schedule and rooms. Starting the flow opens it too.',
    },
};

/**
 * The admin's high-level control over what members get: no gathering (with
 * a message), the waiting room, or the gathering itself. Everything else on
 * the page is setup; this is the door, so it leads the page — one large
 * centred toggle, each state explained on hover. The message only matters
 * while there's no gathering, so only then is there a field for it. Nothing
 * of the setup reaches members until it's open. While the flow runs it has
 * to stay open.
 */
function GatheringStatus({ status, message, onStatus, onMessage, running, sample, saving }) {
    const [draft, setDraft] = useState(message || '');
    useEffect(() => setDraft(message || ''), [message]);

    const disabled = sample ? 'Sample data is only visible to you — members see the real gathering' : null;
    const lockedOpen = running && status === 'open';
    const commitMessage = () => { if ((draft.trim() || null) !== (message || null)) onMessage(draft.trim()); };

    return (
        <div className={`gathering-status is-${status}`}>
            <Tooltip title={disabled || (lockedOpen ? 'Stop the flow before closing the gathering' : null)}>
                <Segmented
                    size="large"
                    className="gathering-status-toggle"
                    value={status}
                    onChange={onStatus}
                    disabled={!!disabled || saving}
                    options={Object.entries(STATES).map(([value, s]) => ({
                        value,
                        label: s.label,
                        icon: s.icon,
                        title: s.text,
                        disabled: lockedOpen && value !== 'open',
                    }))}
                />
            </Tooltip>
            {status === 'closed' && !disabled && (
                <Input.TextArea
                    className="gathering-status-message"
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onBlur={commitMessage}
                    placeholder="Message for members, e.g. “Next gathering: Friday 6pm”"
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    maxLength={500}
                    aria-label="Message members see while there's no gathering"
                />
            )}
        </div>
    );
}

export default GatheringStatus;
